import { useState } from 'react';
import Button from '@components/Button/Button';
import styles from './ContactForm.module.css';

const INITIAL_VALUES = {
  name: '',
  email: '',
  subject: '',
  message: '',
  donation: false,
};

/**
 * Contact form with controlled inputs, field-level validation,
 * and Netlify form submission with optional donation field.
 */
export default function ContactForm() {
  const [formData, setFormData] = useState(INITIAL_VALUES);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  /* ---- Helpers ---- */
  const isValidEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis.';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est requis.';
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = 'Veuillez entrer une adresse email valide.';
    }

    if (!formData.subject.trim()) {
      newErrors.subject = 'Le sujet est requis.';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Le message est requis.';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Le message doit contenir au moins 10 caractères.';
    }

    return newErrors;
  };

  /* ---- Handlers ---- */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Clear field error on change
    if (errors[name]) {
      setErrors((prev) => {
        const copy = { ...prev };
        delete copy[name];
        return copy;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(false);

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);

    try {
      // Prepare form data for Netlify
      const formElement = e.target;
      const netlifyFormData = new FormData(formElement);

      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(netlifyFormData).toString(),
      });

      if (response.ok) {
        setLoading(false);
        setSubmitted(true);
        setFormData(INITIAL_VALUES);
        setErrors({});
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      setLoading(false);
      console.error('Form submission error:', error);
      alert('Une erreur est survenue lors de l\'envoi. Veuillez réessayer.');
    }
  };

  return (
    <form
      className={styles.form}
      onSubmit={handleSubmit}
      noValidate
      name="contact"
      method="POST"
      data-netlify="true"
      netlify-honeypot="bot-field"
    >
      {/* ---- Netlify hidden fields ---- */}
      <input type="hidden" name="form-name" value="contact" />
      <p className={styles.hidden}>
        <label>
          Don&rsquo;t fill this out:{' '}
          <input name="bot-field" />
        </label>
      </p>

      {/* ---- Name ---- */}
      <div className={styles.field}>
        <label htmlFor="contact-name" className={styles.label}>
          Nom complet
        </label>
        <input
          id="contact-name"
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="Votre nom"
          className={`${styles.input} ${errors.name ? styles.inputError : ''}`}
        />
        {errors.name && <span className={styles.error}>{errors.name}</span>}
      </div>

      {/* ---- Email ---- */}
      <div className={styles.field}>
        <label htmlFor="contact-email" className={styles.label}>
          Email
        </label>
        <input
          id="contact-email"
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="votre@email.com"
          className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
        />
        {errors.email && <span className={styles.error}>{errors.email}</span>}
      </div>

      {/* ---- Subject ---- */}
      <div className={styles.field}>
        <label htmlFor="contact-subject" className={styles.label}>
          Sujet
        </label>
        <input
          id="contact-subject"
          type="text"
          name="subject"
          value={formData.subject}
          onChange={handleChange}
          placeholder="Raison de votre message"
          className={`${styles.input} ${errors.subject ? styles.inputError : ''}`}
        />
        {errors.subject && (
          <span className={styles.error}>{errors.subject}</span>
        )}
      </div>

      {/* ---- Message ---- */}
      <div className={styles.field}>
        <label htmlFor="contact-message" className={styles.label}>
          Message
        </label>
        <textarea
          id="contact-message"
          name="message"
          rows={5}
          value={formData.message}
          onChange={handleChange}
          placeholder="Votre message..."
          className={`${styles.textarea} ${errors.message ? styles.inputError : ''}`}
        />
        {errors.message && (
          <span className={styles.error}>{errors.message}</span>
        )}
      </div>

      {/* ---- Donation checkbox ---- */}
      <div className={styles.checkboxField}>
        <input
          id="contact-donation"
          type="checkbox"
          name="donation"
          checked={formData.donation}
          onChange={handleChange}
          className={styles.checkbox}
        />
        <label htmlFor="contact-donation" className={styles.checkboxLabel}>
          Je suis intéressé(e) par l&rsquo;opportunité de faire un don
        </label>
      </div>

      {/* ---- Submit ---- */}
      <Button type="submit" variant="primary" size="lg" disabled={loading}>
        {loading ? 'Envoi en cours…' : 'Envoyer'}
      </Button>

      {/* ---- Success feedback ---- */}
      {submitted && (
        <p className={styles.success}>
          Merci ! Votre message a été envoyé avec succès. Nous vous répondrons dans les plus brefs délais.
        </p>
      )}
    </form>
  );
}
